import { useEffect, useState } from 'react';

const DIALOG_SELECTOR = '[role="dialog"], [role="alertdialog"]';
const POLL_MS = 1000;

/**
 * Is a dialog / drawer / sheet already on screen?
 *
 * An arrival greeting (estimate responses, Office Inbox, route change) must
 * never open on top of something the user is in the middle of — a Log Job
 * drawer, a send flow, a confirm. Every overlay in the app renders a
 * `role="dialog"` (AnimatedModalShell, Vaul/Radix drawers), so the DOM is the
 * one honest registry. A node that is mounted but not laid out (display:none,
 * detached) does not count.
 */
export function isForeignDialogOpen(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc) return false;
  const nodes = doc.querySelectorAll(DIALOG_SELECTOR);
  for (let i = 0; i < nodes.length; i += 1) {
    if (nodes[i].getClientRects().length > 0) return true;
  }
  return false;
}

/**
 * `active` = something wants to greet. While active, re-checks every second
 * so the greeting lands the moment the user closes whatever was open. The
 * read happens during render (read-only) so a greeting can never slip in for
 * a frame before the effect catches up. Inactive → false, no polling.
 */
export function useForeignDialogOpen(active) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), POLL_MS);
    return () => clearInterval(id);
  }, [active]);
  return active ? isForeignDialogOpen() : false;
}

export default useForeignDialogOpen;
